/* ─────────────────────────────────────────────────────────────────────────
   Stage — the single WebGL scene behind the whole homepage story.

   One renderer, one camera, one clock (`s`, see timeline.js). `update()` is a
   pure function of `s`: every object's pose is computed from scratch each
   frame, so scrolling backwards replays the build in reverse instead of
   drifting out of sync. Only the arm's IK is warm-started, and it is
   continuous in `s` by construction.

   World units: 1 = 10 cm. Floor is y = 0.
   ───────────────────────────────────────────────────────────────────────── */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import { Board, LineBuilder, makeLineMaterial, roundedRect } from './board';
import { Enclosure, ENC } from './enclosure';
import { Printer } from './printer';
import { Arm, ARM_SCALE } from './arm';
import { PRODUCTS } from './products';
import { CH, STORY_LEN, clamp, lerp, local, smooth, easeInOut, easeOut, invLerp } from './timeline';

const V = (x, y, z) => new THREE.Vector3(x, y, z);

/* scene layout */
const B = V(-0.75, 0, 0.35);  // where the finished board waits
const E = V(0.75, 0, 0.35);   // printer bed / enclosure
const A = V(0, 0, -0.62);     // arm base
const PED_H = 0.1;
const NODE_R = 0.46;
const NODES = [V(-1.8, 0, -2.25), V(-0.6, 0, -2.55), V(0.6, 0, -2.55), V(1.8, 0, -2.25)];
const SHOW_Y = PED_H; // industry products stand on the pedestal
const PRODUCT_KEYS = ['health', 'gateway', 'rover', 'meter'];

/* The print takes three turns of the mouse wheel: it is spread over PRINT_PX
   of scrolling (a wheel notch is ~100 px), measured in pixels so it is the
   same three scrolls on any screen height. Each scroll lays a third of the
   job. The head doesn't teleport to where scroll says: it glides there,
   easing out, and never faster than the feed rate (a whole job in no less
   than PRINT_SECONDS), so each scroll reads as the printer working.
   Rewinding is quicker. There is no fast-forward: the page holds the story
   at printHold() until the job is done (HomeStory.jsx); the snap to finished
   at Integrate only covers jumps and reloads past it. */
const PRINT_PX = 300;
const PRINT_AT = 0.6;      // screens into Prototype: the printer has risen and the camera is in close
const PRINT_SECONDS = 3.6;   // 1.2 s a lap: slower than this and a steady reader catches the hold
const REWIND = 4;
const PARK_SECONDS = 1.0;
const vhNow = () => (typeof window !== 'undefined' && window.innerHeight) || 900;
const printSpan = () => [CH.prototype[0] + PRINT_AT, CH.prototype[0] + PRINT_AT + PRINT_PX / vhNow()];
/* Where the story waits for an unfinished print: not right after the third
   scroll (the next scroll would hit it and the page would stall, then lurch)
   but at the last point the Prototype copy is still fully shown — the panel
   fades out over the chapter's last ~16% (envelope() in timeline.js), and
   waiting inside that fade leaves the text half-gone. The stretch between
   the third scroll and here gives the machine time to finish and park, so a
   reader at a normal pace never meets it. */
export const printHold = () => CH.prototype[1] - (CH.prototype[1] - CH.prototype[0]) * 0.17;
const APPROACH_SECONDS = 0.7;   // the head's travel move from parked to the first layer
const toward = (v, to, step) => (v < to ? Math.min(to, v + step) : Math.max(to, v - step));

/* camera path: [s, position, target] — eased between keys, held around them */
const CAM = [
  [0.0,  V(0.25, 2.55, 1.05), V(0.0, 0, 0.02)],
  [0.7,  V(0.25, 2.35, 1.0),  V(0.0, 0, 0.02)],
  [2.2,  V(0.1, 1.55, 1.15),  V(0.0, 0, 0.05)],
  [3.2,  V(0.75, 1.15, 1.35), V(0.0, 0.05, 0.02)],
  [4.5,  V(1.05, 0.8, 1.2),   V(0.0, 0.05, 0.0)],
  /* prototype: straight-on front of the machine, the way it is meant to be seen */
  [5, V(0.5, 2.45, 7.6),   V(0.5, 0.95, 0.0)],
  /* …then in close on the bed while it prints, so every layer the nozzle lays reads */
  [5.37,  V(0.75, 2.1, 5.7),   V(0.7, 0.85, 0.2)],
  [CH.prototype[0] + (CH.prototype[1] - CH.prototype[0]) * 0.86, V(0.75, 2.1, 5.9), V(0.7, 0.85, 0.2)],
  /* later keys hang off their chapters, so a chapter's length can change without the camera drifting */
  [CH.integrate[0] + 0.5, V(0.35, 2.9, 5.6),   V(0.0, 0.85, 0.0)],
  [CH.integrate[0] + 2.7, V(-0.5, 2.7, 5.2),   V(0.05, 0.75, 0.1)],
  [CH.activate[0] + 0.5,  V(2.0, 1.55, 3.0),   V(0.75, 0.3, 0.35)],
  [CH.validate[0],        V(2.1, 1.45, 2.8),   V(0.75, 0.3, 0.35)],
  [CH.validate[0] + 1,    V(-0.3, 1.5, 2.9),   V(0.7, 0.3, 0.35)],
  [CH.deploy[0],          V(-0.1, 1.6, 3.0),   V(0.7, 0.3, 0.35)],
  [CH.deploy[0] + 1.1,    V(-0.35, 3.9, 6.3),  V(-0.35, 0.35, -1.2)],
  [CH.deploy[0] + 1.7,    V(-0.35, 3.75, 6.05), V(-0.35, 0.35, -1.25)],
  /* industries: one product at a time on the pedestal, the camera drifting slowly round it */
  [CH.ind0[0] + 0.5, V(0.95, 1.5, 3.35),  V(0, 0.64, 0)],
  [CH.ind3[1] - 0.3, V(-0.95, 1.5, 3.35), V(0, 0.64, 0)],
  [CH.caps[0] + 0.05, V(0.9, 2.3, 4.7),   V(-0.05, 0.6, 0)],
  [CH.caps[0] + 0.7, V(1.0, 2.2, 4.3),    V(0, 0.62, 0)],
  [CH.caps[1],       V(-1.0, 2.1, 4.3),   V(0, 0.62, 0)],
];

/* horizontal framing: fraction of the width the scene is pushed right (text sits left) */
function offsetAt(s) {
  if (s >= CH.ind3[1] - 0.2) return 0; // capabilities: product centred between title and list
  if (s < CH.idea[0] + 0.4) return 0.25; // the hero headline is wide
  return 0.16;
}

function camAt(s, pos, tgt) {
  let i = 0; while (i < CAM.length - 1 && CAM[i + 1][0] <= s) i++;
  const a = CAM[i], b = CAM[Math.min(i + 1, CAM.length - 1)];
  const t = b === a ? 0 : easeInOut(clamp((s - a[0]) / (b[0] - a[0])));
  pos.lerpVectors(a[1], b[1], t); tgt.lerpVectors(a[2], b[2], t);
}

/* blueprint floor */
function makeGrid() {
  const mat = new THREE.ShaderMaterial({
    transparent: true, depthWrite: false,
    uniforms: { uCenter: { value: new THREE.Vector3() }, uOpacity: { value: 1 }, uRadius: { value: 5 } },
    vertexShader: `varying vec3 vW; void main(){ vec4 w = modelMatrix * vec4(position,1.); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: `
      uniform vec3 uCenter; uniform float uOpacity, uRadius; varying vec3 vW;
      float line(float size, float w){ vec2 c = vW.xz / size; vec2 g = abs(fract(c - 0.5) - 0.5) / fwidth(c); return 1.0 - min(min(g.x, g.y) / w, 1.0); }
      void main(){
        float fade = 1.0 - smoothstep(uRadius * 0.35, uRadius, distance(vW.xz, uCenter.xz));
        float a = line(0.1, 1.0) * 0.07 + line(0.5, 1.2) * 0.12;
        gl_FragColor = vec4(0.07, 0.1, 0.2, a * fade * uOpacity);
      }`,
  });
  const m = new THREE.Mesh(new THREE.PlaneGeometry(60, 60), mat);
  m.rotation.x = -Math.PI / 2; m.position.y = -0.001; m.renderOrder = -1;
  return m;
}

export default class Stage {
  constructor(canvas, { mobile = false } = {}) {
    this.mobile = mobile;
    const r = this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
    r.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.5 : 2));
    r.outputColorSpace = THREE.SRGBColorSpace;
    r.toneMapping = THREE.NeutralToneMapping || THREE.ACESFilmicToneMapping;
    r.toneMappingExposure = 1.08;
    r.localClippingEnabled = true;
    r.shadowMap.enabled = !mobile;
    r.shadowMap.type = THREE.PCFSoftShadowMap;
    r.setClearColor(0x000000, 0); // the studio backdrop is CSS, behind the canvas

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.Fog(0xeef0f3, 12, 26);
    const pm = new THREE.PMREMGenerator(r);
    this.scene.environment = pm.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 0.75;

    this.camera = new THREE.PerspectiveCamera(32, 1, 0.02, 60);
    this.camPos = V(0, 3, 2); this.camTgt = V();

    const hemi = new THREE.HemisphereLight(0xffffff, 0xdfe3ea, 0.55); this.scene.add(hemi);
    const key = this.key = new THREE.DirectionalLight(0xffffff, 2.1);
    key.castShadow = !mobile;
    key.shadow.mapSize.set(2048, 2048);
    const sc = key.shadow.camera; sc.left = -3; sc.right = 3; sc.top = 3; sc.bottom = -3; sc.near = 0.5; sc.far = 20;
    key.shadow.bias = -0.0004; key.shadow.normalBias = 0.02; key.shadow.radius = 7; key.shadow.blurSamples = 16;
    this.scene.add(key, key.target);
    const rim = new THREE.DirectionalLight(0xffe8e8, 0.6); rim.position.set(-4, 3, -5); this.scene.add(rim);

    this.grid = makeGrid(); this.scene.add(this.grid);
    const shadow = new THREE.Mesh(new THREE.PlaneGeometry(40, 40), new THREE.ShadowMaterial({ opacity: 0.08 }));
    shadow.rotation.x = -Math.PI / 2; shadow.receiveShadow = true; this.scene.add(shadow);
    this.shadowPlane = shadow;

    this.s = 0; this.sTarget = 0; this.cta = -1; this.intro = 0;
    this.printK = 0; this.parkK = 0; this.approachK = 0;   // the print as the machine has actually laid it
    this.hover = null; this.autoLayer = null;
    this.labels = {};
    this.clock = new THREE.Clock();
    this.ready = false;
  }

  async load(onProgress) {
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    const prog = { board: 0, arm: 0, printer: 0 };
    const tick = () => onProgress && onProgress(prog.board * 0.55 + prog.arm * 0.15 + prog.printer * 0.3);
    const get = (url, k) => loader.loadAsync(url, e => { if (e.total) { prog[k] = e.loaded / e.total; tick(); } });
    const [bg, ag, pg] = await Promise.all([
      get('/models/zx-board.glb', 'board'),
      get('/models/zx-arm.glb', 'arm'),
      get('/models/zx-printer.glb', 'printer'),
    ]);
    prog.board = prog.arm = prog.printer = 1; tick();
    this.build(bg, ag, pg);
    this.warmUp();
    this.ready = true;
  }

  build(bg, ag, pg) {
    const S = this.scene;
    this.board = new Board(bg); S.add(this.board.root);
    const bb = new THREE.Box3().setFromObject(this.board.root);
    this.boardLift = -bb.min.y + 0.002;          // rest the lowest part on the floor
    this.boardTop = bb.max.y;                     // tallest part above the board face
    this.board.setShadows(!this.mobile);

    this.enc = new Enclosure(); S.add(this.enc.root);
    this.enc.seat.position.y = ENC.FLOOR + this.boardLift + 0.004;
    this.printer = new Printer(pg); S.add(this.printer.root);
    this.arm = new Arm(ag); S.add(this.arm.root);
    this.arm.root.position.copy(A);
    this.arm.root.rotation.y = 0;

    /* pickup / seat poses for the choreography */
    this.qDown = new THREE.Quaternion().setFromAxisAngle(V(1, 0, 0), Math.PI);
    // jaws close on the board's short (Z) edges, pads centred on the laminate
    this.arm.jawClosed = (this.board.h / 2) / ARM_SCALE;
    this.grip = -0.008;
    this.tcpW = V();
    this.boardAtB = new THREE.Matrix4().compose(V(B.x, this.boardLift, B.z), new THREE.Quaternion(), V(1, 1, 1));
    const seatY = this.enc.seat.position.y;
    this.boardAtSeat = new THREE.Matrix4().compose(V(E.x, seatY, E.z), new THREE.Quaternion(), V(1, 1, 1));
    this.pickTcp = V(B.x, this.boardLift + this.grip, B.z);
    this.seatTcp = V(E.x, seatY + this.grip, E.z);
    const tcpM = new THREE.Matrix4().compose(this.pickTcp, this.qDown, V(1, 1, 1));
    this.gripOffset = tcpM.clone().invert().multiply(this.boardAtB);
    this.home = V(A.x, 1.3, A.z + 0.6);   // a relaxed ready pose, comfortably inside the reach
    /* Waypoints solved once, each seeded from the one before, so the whole
       pick-and-place runs in a single consistent posture (no wrist flips). */
    let seedN = 7; const rand = () => { seedN = (seedN * 16807) % 2147483647; return (seedN - 1) / 2147483646; };
    const up = 0.5;
    this.wp = {};
    this.wpPos = {
      home: this.home,
      hovB: this.pickTcp.clone().setY(this.pickTcp.y + up),
      pick: this.pickTcp,
      hovE: this.seatTcp.clone().setY(this.seatTcp.y + up),
      seat: this.seatTcp,
    };
    this.wpPos.mid = V((B.x + E.x) / 2, Math.max(this.wpPos.hovB.y, this.wpPos.hovE.y) + 0.35, (B.z + E.z) / 2);
    this.wp.home = this.arm.solveWaypoint(this.wpPos.home, this.qDown, null, rand);
    this.wp.hovB = this.arm.solveWaypoint(this.wpPos.hovB, this.qDown, this.wp.home, rand);
    this.wp.pick = this.arm.solveWaypoint(this.wpPos.pick, this.qDown, this.wp.hovB, rand);
    this.wp.mid = this.arm.solveWaypoint(this.wpPos.mid, this.qDown, this.wp.hovB, rand);
    this.wp.hovE = this.arm.solveWaypoint(this.wpPos.hovE, this.qDown, this.wp.mid, rand);
    this.wp.seat = this.arm.solveWaypoint(this.wpPos.seat, this.qDown, this.wp.hovE, rand);
    this.arm.setQ(this.wp.home);

    /* activation ring + validation scanner */
    const ringMat = new THREE.MeshBasicMaterial({ color: 0xff1a1a, transparent: true, opacity: 0, toneMapped: false, depthWrite: false, blending: THREE.AdditiveBlending });
    this.glow = new THREE.Mesh(new THREE.RingGeometry(0.7, 0.735, 96), ringMat);
    this.glow.rotation.x = -Math.PI / 2; this.glow.position.y = 0.004; S.add(this.glow);
    const scanMat = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending,
      uniforms: { uO: { value: 0 } },
      vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.); }',
      fragmentShader: 'uniform float uO; varying vec2 vUv; void main(){ float l = smoothstep(0.0,1.0,vUv.y); float edge = smoothstep(0.93,1.0,vUv.x)+smoothstep(0.07,0.0,vUv.x); gl_FragColor = vec4(1.0,0.1,0.1,(0.18*l + 0.5*edge*l)*uO); }',
    });
    this.scan = new THREE.Mesh(new THREE.PlaneGeometry(1.0, 0.55), scanMat);
    this.scan.rotation.y = Math.PI / 2; S.add(this.scan);
    const scanLine = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.005, 0.005), new THREE.MeshBasicMaterial({ color: 0xff2020, toneMapped: false }));
    scanLine.position.y = 0.272; this.scan.add(scanLine); // top edge, spanning the plane's width

    /* pedestal, network, products */
    this.pedestal = new THREE.Group(); S.add(this.pedestal);
    const pedMat = new THREE.MeshPhysicalMaterial({ color: 0xf7f8fa, roughness: 0.35, clearcoat: 0.5 });
    const ped = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 1.0, PED_H, 96), pedMat);
    ped.position.y = PED_H / 2; ped.receiveShadow = true; ped.castShadow = true; this.pedestal.add(ped);
    const pedRing = new THREE.Mesh(new THREE.TorusGeometry(0.955, 0.006, 8, 120), new THREE.MeshBasicMaterial({ color: 0xff1a1a, toneMapped: false }));
    pedRing.rotation.x = Math.PI / 2; pedRing.position.y = PED_H + 0.001; this.pedestal.add(pedRing);

    this.netMat = makeLineMaterial(0xff1a1a, 0xff1a1a);
    const L = new LineBuilder();
    this.paths = NODES.map(n => {
      const y = 0.012, side = Math.sign(n.x);
      const pts = [V(side * 0.3, y, -0.92), V(side * 0.3, y, -1.45), V(n.x, y, -1.45), V(n.x, y, n.z + NODE_R)];
      L.poly(pts, 0, 1, 1);
      return pts;
    });
    this.net = L.build(this.netMat); S.add(this.net);
    this.pulses = [];
    const pulseGeo = new THREE.SphereGeometry(0.018, 10, 8), pulseMat = new THREE.MeshBasicMaterial({ color: 0xff2a2a, toneMapped: false });
    for (let i = 0; i < 12; i++) { const p = new THREE.Mesh(pulseGeo, pulseMat); S.add(p); this.pulses.push(p); }

    this.nodes = NODES.map((n, i) => {
      const g = new THREE.Group(); g.position.copy(n); S.add(g);
      const plat = new THREE.Mesh(new THREE.CylinderGeometry(NODE_R, NODE_R + 0.02, 0.05, 72), pedMat);
      plat.position.y = 0.025; plat.receiveShadow = true; g.add(plat);
      const ring = new THREE.Mesh(new THREE.TorusGeometry(NODE_R + 0.005, 0.005, 8, 90), pedRing.material);
      ring.rotation.x = Math.PI / 2; ring.position.y = 0.051; g.add(ring);
      const prod = PRODUCTS[PRODUCT_KEYS[i]]();
      prod.root.traverse(o => { if (o.isMesh) { o.castShadow = !this.mobile; o.receiveShadow = true; } });
      S.add(prod.root);
      return { g, prod };
    });

    /* CTA ghost: the product redrawn as its own blueprint */
    const edges = new LineBuilder();
    const { W, D, H, R, WALL, FLOOR, LID } = ENC;
    const ring = (y, inset, o0, o1) => edges.poly(roundedRect(-W / 2 + inset, -D / 2 + inset, W / 2 - inset, D / 2 - inset, Math.max(0.01, R - inset), y, 8), o0, o1, 0, true);
    ring(0, 0, 0.0, 0.18); ring(FLOOR, WALL, 0.08, 0.24);
    [[-1, -1], [1, -1], [1, 1], [-1, 1]].forEach(([sx, sz], i) => {
      const x = sx * (W / 2 - R * 0.29), z = sz * (D / 2 - R * 0.29);
      edges.seg(V(x, 0, z), V(x, H + LID, z), 0.18 + i * 0.03, 0.42 + i * 0.03);
    });
    ring(H, 0, 0.4, 0.56); ring(H, WALL, 0.44, 0.6); ring(H + LID, 0, 0.55, 0.72);
    edges.poly(roundedRect(-0.35, -0.23, 0.35, 0.23, 0.035, H + LID + 0.006, 4), 0.68, 0.84, 1, true);
    const dz = D / 2 + 0.18;
    edges.seg(V(-W / 2, 0, D / 2 + 0.05), V(-W / 2, 0, dz + 0.03), 0.78, 0.82); edges.seg(V(W / 2, 0, D / 2 + 0.05), V(W / 2, 0, dz + 0.03), 0.78, 0.82);
    edges.seg(V(-W / 2, 0, dz), V(W / 2, 0, dz), 0.82, 0.95, 1);
    const dx = W / 2 + 0.18;
    edges.seg(V(W / 2 + 0.05, 0, D / 2), V(dx + 0.03, 0, D / 2), 0.8, 0.84); edges.seg(V(W / 2 + 0.05, H + LID, D / 2), V(dx + 0.03, H + LID, D / 2), 0.8, 0.84);
    edges.seg(V(dx, 0, D / 2), V(dx, H + LID, D / 2), 0.84, 0.97, 1);
    this.ghostMat = makeLineMaterial(0x0a0a14, 0xff0004);
    this.ghost = edges.build(this.ghostMat); S.add(this.ghost);

    this.fadeables = [];
    [this.enc.root, this.board.root].forEach(r => r.traverse(o => { if (o.isMesh && o.material && !this.fadeables.includes(o.material)) this.fadeables.push(o.material); }));
  }

  /* Compile every shader up front. Most of the story's objects stay hidden
     until their chapter, and three.js only compiles what it draws — so the
     printed part, the printer's clipped shell and the rest would each stall a
     frame the first time they appear, right in the middle of the scroll. */
  warmUp() {
    const hidden = [];
    this.scene.traverse(o => { if (!o.visible) { hidden.push(o); o.visible = true; } });
    this.printer.setReveal(0.5);   // attach the printer's clip so its clipped variants compile too
    try { this.renderer.compile(this.scene, this.camera); } catch (e) { /* first appearance compiles instead */ }
    hidden.forEach(o => { o.visible = false; });
  }

  resize(w, h) {
    this.w = w; this.h = h;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    const portrait = w / h < 0.9;
    this.portrait = portrait;
    if (w > 900) this.camera.setViewOffset(w, h, -w * 0.16, 0, w, h);
    else if (portrait) this.camera.setViewOffset(w, h, 0, h * 0.2, w, h);
    else this.camera.clearViewOffset();
    this.camera.updateProjectionMatrix();
  }

  project(v) {
    const p = v.clone().project(this.camera);
    return { x: (p.x * 0.5 + 0.5) * this.w, y: (-p.y * 0.5 + 0.5) * this.h, z: p.z };
  }

  /* ── the whole choreography, as a function of s ─────────────────────── */
  update(dt, time) {
    if (!this.ready) return;
    this.intro = Math.min(1, this.intro + dt / 2.4);
    const k = 1 - Math.exp(-dt * 7);
    this.s += (this.sTarget - this.s) * k;
    if (Math.abs(this.sTarget - this.s) < 1e-4) this.s = this.sTarget;
    const s = this.s, cta = this.cta;

    const hero = local(s, 'hero'), idea = local(s, 'idea'), build = local(s, 'build'), proto = local(s, 'prototype');
    const integ = local(s, 'integrate'), act = local(s, 'activate'), val = local(s, 'validate'), dep = local(s, 'deploy');
    const caps = local(s, 'caps');

    /* ─ board: blueprint → bare core → copper → solder mask → silkscreen → parts ─ */
    const reveal = Math.max(this.intro * 0.17, 0.17 + 0.83 * smooth(invLerp(0, 0.85, idea)));
    this.board.setReveal(s < CH.idea[0] ? this.intro * 0.17 + hero * 0.02 : reveal);
    const linesOn = 1 - invLerp(0.08, 0.2, build);
    this.board.setSubstrate(smooth(invLerp(0.0, 0.14, build)));
    this.board.setLayers(invLerp(0.1, 0.36, build), smooth(invLerp(0.36, 0.52, build)), invLerp(0.5, 0.62, build));
    this.board.setPlacement(invLerp(0.6, 0.98, build));

    const boardM = new THREE.Matrix4();
    const lift = lerp(0.002, this.boardLift, smooth(invLerp(0, 0.2, build)));
    // board path: centre → B (prototype) → arm → seat (integrate)
    const toB = easeInOut(invLerp(0.0, 0.28, proto));
    const bp = V(lerp(0, B.x, toB), lift, lerp(0, B.z, toB));
    boardM.compose(bp, new THREE.Quaternion(), V(1, 1, 1));

    /* ─ enclosure: printed at E, later moved onto the pedestal ─ */
    const encToCentre = easeInOut(invLerp(0.05, 0.4, dep));
    const pedRise = easeOut(invLerp(0.0, 0.3, dep));
    this.pedestal.position.y = (pedRise - 1) * PED_H * 1.2;
    // capabilities: a smaller stand so its ring stays clear of the title and the list
    this.pedestal.scale.setScalar(1 - 0.32 * smooth(invLerp(CH.ind3[1] - 0.2, CH.caps[0] + 0.4, s)));
    this.pedestal.visible = pedRise > 0;
    this.enc.root.position.set(lerp(E.x, 0, encToCentre), encToCentre * PED_H, lerp(E.z, 0, encToCentre));
    // the core steps off the pedestal while the four industry products take their turn on it
    const indIn = s >= CH.ind0[0] ? smooth(invLerp(CH.ind0[0], CH.ind0[0] + 0.45, s)) : 0;
    const encAway = indIn * (1 - smooth(invLerp(CH.caps[0], CH.caps[0] + 0.5, s)));
    this.enc.root.scale.setScalar(Math.max(0.001, 1 - encAway));
    /* the print chases where scroll wants it, no faster than the feed rate */
    const [print0, print1] = printSpan();
    const printWant = invLerp(print0, print1, s);
    /* before the first layer the head travels in from its parked spot; no
       plastic goes down until it has arrived */
    if (s < CH.prototype[0]) this.approachK = 0;
    else if (s >= CH.integrate[0]) this.approachK = 1;
    else this.approachK = toward(this.approachK, printWant > 0 || this.printK > 0 ? 1 : 0, dt / APPROACH_SECONDS);
    if (s < CH.prototype[0]) this.printK = 0;
    else if (s >= CH.integrate[0]) this.printK = 1;
    else if (this.approachK < 1) this.printK = 0;
    else {
      const gap = Math.abs(printWant - this.printK);
      const cap = ((printWant > this.printK ? 1 : REWIND) / PRINT_SECONDS) * dt;
      // ease toward the scroll (a small creep so it always lands), capped at the feed rate
      this.printK = toward(this.printK, printWant, Math.min(cap, gap * (1 - Math.exp(-dt * 5)) + 0.01 * dt));
    }
    const printP = this.printK;
    // the head steps aside as soon as the job is done, at machine speed (and back if scrolled back into it)
    const parkWant = printP >= 1 ? 1 : 0;
    this.parkK = s >= CH.integrate[0] ? 1 : toward(this.parkK, parkWant, dt / PARK_SECONDS);

    /* ─ printer ─
       The part must be in its final place BEFORE setPrint runs: the growth is a
       world-space clip plane, so moving the part afterwards would slide it out
       from under its own clip and the tray would only appear once the clip is
       released — a pop at the end instead of a print. */
    const prOn = smooth(invLerp(0.0, 0.16, proto)) * (1 - smooth(invLerp(0.0, 0.14, integ)));
    this.printer.root.position.set(E.x, this.printer.baseLift, E.z);
    this.printer.setReveal(prOn);
    if (prOn > 0) {
      this.printer.update(printP, this.parkK, time, this.approachK);
      // the part is printed on the bed, so it sits at bed height and rides the throw
      const offBed = s >= CH.integrate[0] ? smooth(invLerp(0, 0.12, integ)) : 0;
      this.enc.root.position.y += this.printer.bedTop * (1 - offBed);
      if (printP > 0) this.enc.root.position.add(this.printer.bedOffset.clone().multiplyScalar(1 - offBed));
    }

    this.enc.setPrint(s >= CH.integrate[0] ? 1 : printP);
    // banding is loud on the bed, then settles to the trace a real print keeps
    this.enc.setStripe(s < CH.integrate[0]
      ? (printP > 0 ? 1 : 0)
      : lerp(1, 0.22, smooth(invLerp(0.02, 0.3, integ))));
    this.enc.root.visible = (printP > 0 || s >= CH.integrate[0]) && encAway < 0.99;

    /* ─ arm ─ */
    const armOn = smooth(invLerp(0.04, 0.18, integ)) * (1 - smooth(invLerp(0.0, 0.35, act)));
    this.arm.setOpacity(armOn);
    this.arm.root.position.set(A.x, (armOn - 1) * 0.4, A.z);
    let vac = 0;
    if (armOn > 0) {
      const i = integ;
      const W = this.wp, P = this.wpPos;
      /* [from, to, t0, t1, straight] — joint-space moves between solved
         waypoints; `straight` segments are the vertical approaches, kept
         exactly vertical by a few IK iterations on top. */
      const SEG = [
        ['home', 'home', 0, 0.1], ['home', 'hovB', 0.1, 0.24], ['hovB', 'pick', 0.24, 0.31, 1],
        ['pick', 'pick', 0.31, 0.36], ['pick', 'hovB', 0.36, 0.44, 1], ['hovB', 'mid', 0.44, 0.57],
        ['mid', 'hovE', 0.57, 0.7], ['hovE', 'seat', 0.7, 0.78, 1], ['seat', 'seat', 0.78, 0.83],
        ['seat', 'hovE', 0.83, 0.9, 1], ['hovE', 'home', 0.9, 1.0001],
      ];
      let seg = SEG.find(g => i >= g[2] && i < g[3]) || SEG[SEG.length - 1];
      if (s >= CH.activate[0]) seg = ['home', 'home', 0, 1];
      const t = easeInOut(clamp((i - seg[2]) / (seg[3] - seg[2])));
      const qa = W[seg[0]], qb = W[seg[1]];
      this.arm.setQ(qa.map((x, k) => x + (qb[k] - x) * t));
      let tgt = P[seg[0]].clone().lerp(P[seg[1]], t);
      if (!seg[4]) { this.arm.tcp.getWorldPosition(this.tcpW); tgt = this.tcpW.clone(); }
      if (seg[4]) this.arm.solve(tgt, this.qDown, 6);
      this.armTgt = tgt;
      // jaws: open on approach, close on the board at the bottom, open again once it is seated
      vac = s < CH.activate[0]
        ? smooth(invLerp(0.315, 0.35, i)) * (1 - 0.55 * smooth(invLerp(0.785, 0.82, i)) - 0.45 * smooth(invLerp(0.87, 0.91, i)))
        : 0; // release just enough to let go inside the tray, open fully once clear of the walls
      this.arm.setGrip(vac);
      this.arm.tcp.getWorldPosition(this.tcpW);
    }
    if (s >= CH.integrate[0]) {
      const grabbed = integ >= 0.35 && integ < 0.78;
      if (grabbed) {
        // the arm model is scaled; rebuild the TCP frame at unit scale so the grip offset stays true
        const [tp, tq] = this.arm.tcpPose(V(), new THREE.Quaternion());
        boardM.multiplyMatrices(new THREE.Matrix4().compose(tp, tq, V(1, 1, 1)), this.gripOffset);
      } else if (integ >= 0.78) {
        // seated: ride along with the enclosure (and its exploded board layer)
        this.enc.root.updateMatrixWorld(true);
        boardM.copy(this.enc.seat.matrixWorld);
      } else boardM.copy(this.boardAtB);
    }
    boardM.decompose(this.board.root.position, this.board.root.quaternion, this.board.root.scale);
    // sealed inside the closed enclosure from power-on until it is exploded again: skip ~350k triangles
    this.board.root.visible = !(s >= CH.activate[0] && s < CH.caps[0] + 0.25 && cta < 0);
    this.board.setLines(Math.max(linesOn * (s < CH.prototype[0] ? 1 : 0), 0));

    /* ─ lid, power, validation ─ */
    const lidK = easeInOut(invLerp(0.9, 1, integ));
    this.enc.setLid(s >= CH.integrate[0] ? lidK : 0, (1 - lidK) * 0.9);
    const lidMats = [];
    this.enc.lid.traverse(o => { if (o.isMesh) lidMats.push(o.material); });
    lidMats.forEach(m => { m.transparent = lidK < 1; m.opacity = smooth(invLerp(0, 0.4, lidK)); });

    const boot = invLerp(0.12, 0.55, act), live = s >= CH.activate[0] + (CH.activate[1] - CH.activate[0]) * 0.55;
    const testing = s >= CH.validate[0] && val < 0.72, pass = val >= 0.72;
    let mode = 'off';
    if (s >= CH.activate[0] && boot > 0) mode = boot < 1 && !live ? 'boot' : 'live';
    if (testing && s >= CH.validate[0]) mode = 'test';
    if (s >= CH.validate[0] && pass) mode = 'pass';
    if (s >= CH.deploy[0]) mode = 'live';
    if ((this._scrT = (this._scrT || 0) + dt) > 1 / 24 || mode !== this._mode) {
      this._scrT = 0; this._mode = mode;
      this.enc.drawScreen(time, mode, boot, {});
    }
    this.enc.setLeds(s >= CH.activate[0] ? invLerp(0.2, 0.9, act) : 0, pass || s >= CH.deploy[0]);
    const glowA = s >= CH.activate[0] ? smooth(invLerp(0.1, 0.5, act)) * (1 - encToCentre) : 0;
    this.glow.position.set(this.enc.root.position.x, 0.004, this.enc.root.position.z);
    this.glow.material.opacity = glowA * (0.55 + 0.25 * Math.sin(time * 3));
    this.glow.visible = glowA > 0.01;
    const scanOn = s >= CH.validate[0] && s < CH.deploy[0] ? smooth(invLerp(0.02, 0.1, val)) * (1 - smooth(invLerp(0.68, 0.76, val))) : 0;
    this.scan.visible = scanOn > 0.01;
    this.scan.material.uniforms.uO.value = scanOn;
    const sweep = Math.sin(invLerp(0.05, 0.72, val) * Math.PI * 2 - Math.PI / 2) * 0.5 + 0.5;
    this.scan.position.set(E.x - ENC.W * 0.55 + sweep * ENC.W * 1.1, 0.275, E.z);

    /* ─ deploy network → one product at a time on the pedestal ─ */
    const netOn = s >= CH.deploy[0] ? smooth(invLerp(0.3, 0.7, dep)) : 0;
    const netOut = indIn;
    this.netMat.uniforms.uReveal.value = netOn;
    this.netMat.uniforms.uOpacity.value = 1 - netOut;
    this.net.visible = netOn > 0 && netOut < 1;
    const travel = s >= CH.deploy[0] ? easeInOut(invLerp(0.45, 0.92, dep)) : 0;
    const nodeOn = s >= CH.deploy[0] ? smooth(invLerp(0.3, 0.6, dep)) * (1 - netOut) : 0;
    const showPos = V(0, SHOW_Y, 0);
    this.nodes.forEach((n, i) => {
      n.g.visible = nodeOn > 0.01;
      n.g.scale.setScalar(Math.max(0.001, nodeOn));
      const [w0, w1] = CH[`ind${i}`];
      const pos = V(); let sc = 0, spin = 0;
      if (s < w0) {
        // deploy: travelling out along its line to its station
        if (travel >= 1) pos.copy(n.g.position).setY(0.05); else this.along(this.paths[i], travel, pos).setY(0.05 * travel);
        sc = travel * 0.5 * (i === 0 ? 1 : 1 - netOut);
      } else if (s < w1) {
        // its own chapter: onto the pedestal, full size, turning slowly
        const indK = local(s, `ind${i}`);
        const kin = easeInOut(invLerp(0, 0.22, indK)), kout = easeInOut(invLerp(0.84, 1, indK));
        const from = i === 0 ? n.g.position.clone().setY(0.05) : showPos;
        pos.lerpVectors(from, showPos, kin);
        pos.y += Math.sin(kin * Math.PI) * 0.25;
        sc = lerp(i === 0 ? 0.5 : 0, 1.15, kin) * (1 - kout);
        spin = (1 - kin) * 1.2 + Math.sin(time * 0.45) * 0.45; // sway, but keep the face to camera
      }
      if (s >= CH.caps[0] || cta >= 0) sc = 0;
      n.prod.root.visible = sc > 0.01;
      n.prod.root.position.copy(pos);
      n.prod.root.scale.setScalar(Math.max(0.001, sc));
      n.prod.root.rotation.y = (PRODUCT_KEYS[i] === 'rover' ? -Math.PI / 2 + 0.5 : 0) + Math.sin(time * 0.4 + i) * 0.08 + spin;
      if (n.prod.root.visible) n.prod.draw(time);
    });
    this.pulses.forEach((p, j) => {
      const path = this.paths[j % 4];
      const u = ((time * 0.35 + j * 0.27) % 1);
      p.visible = netOn >= 1 && netOut < 0.5;
      this.along(path, u, p.position);
    });

    /* ─ capabilities: explode + highlight ─ */
    const ex = s >= CH.caps[0] ? easeInOut(invLerp(0.12, 0.42, caps)) : 0;
    this.enc.explode(ex);
    this.enc.highlight(this.hover || this.autoLayer, s >= CH.caps[0] ? smooth(invLerp(0.3, 0.45, caps)) : 0);
    if (s >= CH.caps[0]) this.enc.root.rotation.y = Math.sin(time * 0.25) * 0.08 + (caps - 0.5) * 0.4;
    else this.enc.root.rotation.y = 0;

    /* ─ CTA: the product dissolves back into its blueprint ─ */
    const ghostK = cta >= 0 ? smooth(invLerp(0.1, 0.6, cta)) : 0;
    this.ghost.visible = ghostK > 0;
    if (cta >= 0) {
      this.enc.explode(1 - smooth(invLerp(0, 0.3, cta)));
      this.enc.root.rotation.y = time * 0.15;
      this.ghost.rotation.y = time * 0.15;
      this.ghostMat.uniforms.uReveal.value = ghostK;
      this.ghostMat.uniforms.uOpacity.value = ghostK;
      this.nodes.forEach(n => { n.g.visible = false; n.prod.root.visible = false; });
      this.net.visible = false; this.pulses.forEach(p => { p.visible = false; });
      this.pedestal.visible = false; this.glow.visible = false; this.scan.visible = false;
      this.ghost.position.copy(this.enc.root.position);
      this.board.setLines(ghostK);
      this.board.setReveal(1);
    }
    const solid = 1 - ghostK;
    this.fadeables.forEach(m => {
      if (m.userData.baseTransparent === undefined) m.userData.baseTransparent = m.transparent;
      if (m.userData.fadeOwned || solid < 1) {
        m.userData.fadeOwned = solid < 1;
        m.transparent = solid < 1 || m.userData.baseTransparent;
        m.opacity = solid;
      }
    });
    if (cta >= 0) {
      const on = solid > 0.02;
      this.enc.root.visible = on;
      this.board.substrate.visible = on;
      this.board.layerGroup.visible = on;
      this.board.waves.forEach(w => { w.group.visible = on; });
    }
    this.shadowPlane.material.opacity = 0.08 * solid;

    /* blueprint grid: strong while the board is being designed, a whisper afterwards */
    const gridO = cta >= 0 ? 0.25 + 0.75 * ghostK : lerp(1, 0.14, smooth(invLerp(CH.build[1] - 0.5, CH.prototype[0] + 0.6, s)));
    this.grid.material.uniforms.uOpacity.value = gridO;
    if (this.w > 900) {
      const want = cta >= 0 ? 0.16 : offsetAt(s);
      this.offX = this.offX === undefined ? want : this.offX + (want - this.offX) * (1 - Math.exp(-dt * 4));
      this.camera.setViewOffset(this.w, this.h, -this.w * this.offX, 0, this.w, this.h);
    }

    /* ─ camera ─ */
    const pos = V(), tgt = V();
    if (cta >= 0) {
      const a = time * 0.1;
      const r = 3.1 - cta * 0.5;
      pos.set(Math.sin(a) * r, 1.9 - cta * 0.3, Math.cos(a) * r); tgt.set(0, 0.3, 0);
    } else camAt(s, pos, tgt);
    /* Integrate: move in close and follow the gripper, so the pick and the
       placement both fill the frame; ease back out as the product powers on. */
    if (cta < 0 && s >= CH.integrate[0] && s < CH.activate[1]) {
      const w = smooth(invLerp(0.06, 0.2, integ)) * (1 - smooth(invLerp(0.02, 0.45, act)));
      if (w > 0) {
        const t = this.tcpW;
        const onArm = V(t.x * 0.8, clamp(t.y * 0.55 + 0.12, 0.3, 0.9), t.z * 0.6 + 0.12);
        // once the board is released, hand the shot over to the enclosure for the lid
        const focus = onArm.lerp(V(E.x, 0.3, E.z), smooth(invLerp(0.8, 0.92, integ)));
        tgt.lerp(focus, w);
        pos.lerp(focus.clone().add(V(0.85, 1.75, 3.45)), w);
      }
    }
    if (this.portrait) pos.sub(tgt).multiplyScalar(1.95).add(tgt);
    // a touch of parallax from the pointer
    if (this.pointer && !this.portrait) { pos.x += this.pointer.x * 0.06; pos.y += this.pointer.y * 0.04; }
    this.camera.position.copy(pos); this.camera.lookAt(tgt);
    // the key follows the shot, but only in whole shadow-map texels: a shadow
    // map that slides by fractions of a texel makes every edge shimmer on scroll
    const kd = V(2.5, 5, 3.2), kz = kd.clone().normalize();
    const kx = V(0, 1, 0).cross(kz).normalize(), ky = kz.clone().cross(kx);
    const tex = (this.key.shadow.camera.right - this.key.shadow.camera.left) / this.key.shadow.mapSize.x;
    const snap = v => Math.round(v / tex) * tex;
    const kt = new THREE.Vector3()
      .addScaledVector(kx, snap(tgt.dot(kx))).addScaledVector(ky, snap(tgt.dot(ky))).addScaledVector(kz, tgt.dot(kz));
    this.key.position.copy(kt).add(kd); this.key.target.position.copy(kt);
    this.grid.material.uniforms.uCenter.value.copy(tgt);
    this.grid.material.uniforms.uRadius.value = 3.5 + pos.distanceTo(tgt) * 1.2;

    /* labels the DOM pins to */
    this.board.root.updateMatrixWorld(true);
    const bw = p => p.clone().applyMatrix4(this.board.root.matrixWorld);
    const L = this.labels;
    L.dimW = this.project(bw(this.board.anchors.dimW));
    L.dimH = this.project(bw(this.board.anchors.dimH));
    L.fpga = this.project(bw(this.board.anchors.fpga.clone().setY(0.05)));
    L.enc = this.project(this.enc.root.position.clone().add(V(0, 0.45, 0)));
    L.nodes = this.nodes.map(n => this.project(n.g.position.clone().add(V(0, 0.82, 0))));

    this.renderer.render(this.scene, this.camera);
  }

  along(pts, u, out) {
    let L = 0; const seg = [];
    for (let i = 1; i < pts.length; i++) { const d = pts[i].distanceTo(pts[i - 1]); seg.push(d); L += d; }
    let d = clamp(u) * L, i = 0;
    while (i < seg.length - 1 && d > seg[i]) { d -= seg[i]; i++; }
    return out.lerpVectors(pts[i], pts[i + 1], Math.min(1, d / seg[i]));
  }

  dispose() {
    this.renderer.dispose();
    this.scene.traverse(o => { if (o.geometry) o.geometry.dispose(); });
  }
}

export { STORY_LEN };
